import React from 'react';
import HomeNav from '../navigations/homenav';
import Footer from '../navigations/Footer';
import {
  LightBulbIcon,
  EyeIcon,
  HeartIcon,
  ShieldCheckIcon,
  GlobeAltIcon,
  UserGroupIcon,
  AcademicCapIcon,
  HandRaisedIcon
} from '@heroicons/react/24/outline';
import { useLanguageContext } from '../navigations/homenav';

// Mission Card Component
const MissionCard = ({ icon, title, description }) => (
  <div className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
    <div className="flex items-center justify-center h-16 w-16 rounded-full bg-teal-100 text-teal-600 mb-6">
      {icon}
    </div>
    <h3 className="text-xl font-bold text-gray-800 mb-4">{title}</h3>
    <p className="text-gray-600 leading-relaxed">{description}</p>
  </div>
);

// Value Card Component
const ValueCard = ({ icon, title, description }) => (
  <div className="text-center bg-gray-50 p-6 rounded-lg hover:bg-gray-100 transition-colors duration-300">
    <div className="flex items-center justify-center h-12 w-12 rounded-md bg-sky-100 text-sky-600 mx-auto mb-4">
      {icon}
    </div>
    <h3 className="text-lg font-semibold text-gray-800 mb-2">{title}</h3>
    <p className="text-gray-600 text-sm">{description}</p>
  </div>
);

// Vision Section Component
const VisionSection = () => {
  const { t } = useLanguageContext();
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 text-center">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            {t('mvvVisionTitle')}
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            {t('mvvVisionDesc')}
          </p>
        </div>
      </div>
    </section>
  );
};

const MissionVisionValuesPage = () => {
  const { t } = useLanguageContext();

  return (
    <div className="bg-gray-50 text-gray-800">
      <HomeNav />

      <main>
        {/* Hero Section */}
        <section
          className="py-24 bg-center text-white relative"
          style={{
            backgroundImage: "url('/aboutimage13.jpeg')",
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            minHeight: "400px"
          }}
        >
          <div className="absolute inset-0 bg-slate-800 bg-opacity-40"></div>
          <div className="max-w-7xl mx-auto px-6 text-center relative z-10">
            <LightBulbIcon className="h-16 w-16 mx-auto mb-6" />
            <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight">
              {t('missionVisionValues')}
            </h1>
            <p className="mt-4 text-lg md:text-xl text-sky-100 max-w-3xl mx-auto">
              {t('mvvHeroSubtitle')}
            </p>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t('mvvMissionTitle')}
              </h2>
              <p className="text-lg text-gray-600 max-w-4xl mx-auto">
                {t('mvvMissionDesc')}
              </p>
            </div>

            <div className="mb-16">
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                {t('mvvWeWorkTo')}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <MissionCard
                  icon={<UserGroupIcon className="h-8 w-8" />}
                  title={t('mvvMissionPoint1Title')}
                  description={t('mvvMissionPoint1Desc')}
                />
                <MissionCard
                  icon={<LightBulbIcon className="h-8 w-8" />}
                  title={t('mvvMissionPoint2Title')}
                  description={t('mvvMissionPoint2Desc')}
                />
                <MissionCard
                  icon={<AcademicCapIcon className="h-8 w-8" />}
                  title={t('mvvMissionPoint3Title')}
                  description={t('mvvMissionPoint3Desc')}
                />
                <MissionCard
                  icon={<HandRaisedIcon className="h-8 w-8" />}
                  title={t('mvvMissionPoint4Title')}
                  description={t('mvvMissionPoint4Desc')}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Vision Section */}
        <VisionSection />

         {/* Impact Section */}
        <section className="py-20 bg-gradient-to-br from-teal-600 to-cyan-600 text-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                {t('mvvImpactTitle')}
              </h2>
              <p className="text-teal-100 text-lg max-w-3xl mx-auto">
               {t('mvvImpactIntro')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16">
              <div className="bg-white bg-opacity-10 p-8 rounded-xl backdrop-blur-sm">
                <h3 className="text-2xl font-bold mb-4 flex items-center">
                  <UserGroupIcon className="h-8 w-8 mr-3" />
                  {t('mvvImpact1Title')}
                </h3>
                <p className="text-teal-100 leading-relaxed">
                  {t('mvvImpact1Desc')}
                </p>
              </div>

              <div className="bg-white bg-opacity-10 p-8 rounded-xl backdrop-blur-sm">
                <h3 className="text-2xl font-bold mb-4 flex items-center">
                  <LightBulbIcon className="h-8 w-8 mr-3" />
                  {t('mvvImpact2Title')}
                </h3>
                <p className="text-teal-100 leading-relaxed">
                  {t('mvvImpact2Desc')}
                </p>
              </div>

              <div className="bg-white bg-opacity-10 p-8 rounded-xl backdrop-blur-sm">
                <h3 className="text-2xl font-bold mb-4 flex items-center">
                  <AcademicCapIcon className="h-8 w-8 mr-3" />
                  {t('mvvImpact3Title')}
                </h3>
                <p className="text-teal-100 leading-relaxed">
                  {t('mvvImpact3Desc')}
                </p>
              </div>

              <div className="bg-white bg-opacity-10 p-8 rounded-xl backdrop-blur-sm">
                <h3 className="text-2xl font-bold mb-4 flex items-center">
                  <HandRaisedIcon className="h-8 w-8 mr-3" />
                  {t('mvvImpact4Title')}
                </h3>
                <p className="text-teal-100 leading-relaxed">
                  {t('mvvImpact4Desc')}
                </p>
              </div>
            </div>

            <div className="text-center">
              <h3 className="text-2xl font-bold mb-8">{t('mvvNetworkTitle')}</h3>
              <p className="text-teal-100 text-lg leading-relaxed max-w-4xl mx-auto">
                {t('mvvNetworkDesc')}
              </p>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t('mvvValuesTitle')}
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                {t('mvvValuesIntro')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <ValueCard
                icon={<UserGroupIcon className="h-6 w-6" />}
                title={t('mvvValue1Title')}
                description={t('mvvValue1Desc')}
              />
              <ValueCard
                icon={<HandRaisedIcon className="h-6 w-6" />}
                title={t('mvvValue2Title')}
                description={t('mvvValue2Desc')}
              />
              <ValueCard
                icon={<ShieldCheckIcon className="h-6 w-6" />}
                title={t('mvvValue3Title')}
                description={t('mvvValue3Desc')}
              />
              <ValueCard
                icon={<HeartIcon className="h-6 w-6" />}
                title={t('mvvValue4Title')}
                description={t('mvvValue4Desc')}
              />
              <ValueCard
                icon={<LightBulbIcon className="h-6 w-6" />}
                title={t('mvvValue5Title')}
                description={t('mvvValue5Desc')}
              />
              <ValueCard
                icon={<UserGroupIcon className="h-6 w-6" />}
                title={t('mvvValue6Title')}
                description={t('mvvValue6Desc')}
              />
            </div>
          </div>
        </section>

         {/* ZOA WaA Program Alignment Section */}
         <section className="py-20 bg-gradient-to-br from-teal-600 to-cyan-600 text-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                {t('mvvAlignmentTitle')}
              </h2>
              <div className="w-24 h-1 bg-white mx-auto mb-8"></div>
            </div>

            <div className="max-w-4xl mx-auto text-center">
              <p className="text-lg md:text-xl leading-relaxed text-teal-100">
                {t('mvvAlignmentDesc')}
              </p>
            </div>
          </div>
        </section>

        {/* Call to Action Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              {t('mvvCtaTitle')}
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              {t('mvvCtaDesc')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contactus"
                className="inline-block px-8 py-3 rounded-md text-lg font-medium bg-sky-600 text-white hover:bg-sky-700 transition-colors duration-300"
              >
                {t('getInvolved')}
              </a>
              <a
                href="/programs"
                className="inline-block px-8 py-3 rounded-md text-lg font-medium border-2 border-sky-600 text-sky-600 hover:bg-sky-600 hover:text-white transition-colors duration-300"
              >
                {t('mvvCtaExplorePrograms')}
              </a>
            </div>
          </div>
        </section>

       
      </main>

      <Footer />
    </div>
  );
};

export default MissionVisionValuesPage;
